import { useState } from 'react';
import {
    Shield,
    Users,
    Clock,
    AlertTriangle,
    CheckCircle,
    DollarSign,
    Lock,
    FileText,
    MessageSquare,
    Calendar,
    Flag,
    ArrowRight,
    AlertCircle,
    CheckSquare
} from 'lucide-react';

const Main = () => {
    const [activeTab, setActiveTab] = useState('conflicts');

    // Mock conflict check data
    const conflictData = {
        status: "checking",
        checks: [
            {
                type: "Client Conflicts",
                status: "complete",
                results: [
                    {
                        company: "TechVision AI",
                        relationship: "Existing Client",
                        conflict: "None",
                        lastChecked: "2 mins ago"
                    }
                ]
            },
            {
                type: "Industry Conflicts",
                status: "in_progress",
                results: [
                    {
                        sector: "AI/ML",
                        competitors: 3,
                        status: "Reviewing"
                    }
                ]
            },
            {
                type: "Team Availability",
                status: "complete",
                results: [
                    {
                        team: "IP Strategy",
                        available: "Yes",
                        nextSlot: "Immediate"
                    }
                ]
            }
        ]
    };

    // Mock milestone data
    const milestones = [
        {
            name: "Initial Assessment",
            status: "complete",
            payment: {
                amount: 2500,
                released: true,
                date: "2024-01-05"
            },
            deliverables: [
                "Portfolio Review",
                "Strategy Document",
                "Initial Assessment Report"
            ]
        },
        {
            name: "Filing Strategy",
            status: "in_progress",
            payment: {
                amount: 3500,
                released: false,
                date: "2024-01-20"
            },
            deliverables: [
                "Filing Timeline",
                "Cost Projections",
                "Priority Recommendations"
            ]
        },
        {
            name: "Implementation",
            status: "pending",
            payment: {
                amount: 5000,
                released: false,
                date: "2024-02-15"
            },
            deliverables: [
                "Filed Applications",
                "Status Report",
                "Next Steps Document"
            ]
        }
    ];

    // Mock collaboration data
    const collaborationTools = {
        activeUsers: [
            {
                name: "Sarah Chen",
                role: "IP Strategy Lead",
                status: "online",
                lastActive: "Now"
            },
            {
                name: "James Wilson",
                role: "Technical Lead",
                status: "away",
                lastActive: "5m ago"
            }
        ],
        documents: [
            {
                name: "IP Strategy Overview",
                type: "Document",
                lastEdited: "10m ago",
                sharedWith: 4
            },
            {
                name: "Filing Timeline",
                type: "Spreadsheet",
                lastEdited: "1h ago",
                sharedWith: 3
            }
        ],
        tasks: [
            {
                name: "Review competitor analysis",
                assignee: "Sarah Chen",
                due: "Tomorrow",
                priority: "high"
            },
            {
                name: "Update filing strategy",
                assignee: "James Wilson",
                due: "Next Week",
                priority: "medium"
            }
        ]
    };

    return (
        <div className="space-y-6">
            {/* Automated Conflict Check */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <Shield className="w-5 h-5" />
                        <span className='font-bold'>Automated Conflict Check</span>
                    </div>
                    <div>
                        Real-time conflict analysis and team availability
                    </div>
                </div>
                <div>
                    <div className="space-y-4 mt-2">
                        {conflictData.checks.map((check, index) => (
                            <div key={index} className="border rounded-lg p-4">
                                <div className="flex justify-between items-start mb-3">
                                    <div>
                                        <h3 className="font-medium">{check.type}</h3>
                                        <p className="text-sm text-gray-500">
                                            Status: {check.status === 'complete' ? 'Completed' : 'In Progress'}
                                        </p>
                                    </div>
                                    {check.status === 'complete' ? (
                                        <div className="text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-[#d1fae5] text-[#065f46] flex items-center">
                                            <CheckCircle className="w-3 h-3 mr-1" />
                                            Verified
                                        </div>
                                    ) : (
                                        <div className='text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm flex items-center bg-blue-100 text-blue-800'>
                                            <Clock className="w-3 h-3 mr-1" />
                                            Checking
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-2 mt-4">
                                    {check.results.map((result, idx) => (
                                        <div key={idx} className="bg-gray-50 p-3 rounded-lg text-sm">
                                            {Object.entries(result).map(([key, value]) => (
                                                <div key={key} className="flex justify-between">
                                                    <span className="text-gray-500 capitalize">{key}:</span>
                                                    <span>{value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Real-time Collaboration */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        <span className='font-bold'> Real-time Collaboration Hub</span>
                    </div>
                </div>
                <div>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                        {/* Active Users */}
                        <div className="border rounded-lg p-4">
                            <h3 className="font-medium mb-3">Active Team Members</h3>
                            <div className="space-y-3">
                                {collaborationTools.activeUsers.map((user, index) => (
                                    <div key={index} className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <div className={`w-2 h-2 rounded-full ${user.status === 'online' ? 'bg-green-500' : 'bg-yellow-500'
                                                }`} />
                                            <div>
                                                <div className="font-medium">{user.name}</div>
                                                <div className="text-sm text-gray-500">{user.role}</div>
                                            </div>
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {user.lastActive}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Active Documents */}
                        <div className="border rounded-lg p-4">
                            <h3 className="font-medium mb-3">Active Documents</h3>
                            <div className="space-y-3">
                                {collaborationTools.documents.map((doc, index) => (
                                    <div key={index} className="flex items-center justify-between">
                                        <div>
                                            <div className="font-medium">{doc.name}</div>
                                            <div className="text-sm text-gray-500">
                                                Last edited: {doc.lastEdited}
                                            </div>
                                        </div>
                                        <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                                            Open
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Task Board */}
                    <div className="mt-4 border rounded-lg p-4">
                        <h3 className="font-medium mb-3">Task Board</h3>
                        <div className="space-y-3">
                            {collaborationTools.tasks.map((task, index) => (
                                <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                                    <div>
                                        <div className="font-medium">{task.name}</div>
                                        <div className="text-sm text-gray-500">
                                            Assigned to: {task.assignee} • Due: {task.due}
                                        </div>
                                    </div>
                                    <div className={`text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm
                    ${task.priority === 'high' ? 'bg-[#fee2e2] text-[#991b1b]' :
                                            'bg-[#fef9c3] text-[#854d0e]'}
                  `}>
                                        {task.priority}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Milestone Payment System */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <Flag className="w-5 h-5" />
                        <span className='font-bold'> Milestone Management</span>
                    </div>
                </div>
                <div>
                    <div className="space-y-4 mt-2">
                        {milestones.map((milestone, index) => (
                            <div key={index} className="border rounded-lg p-4">
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h3 className="font-medium">{milestone.name}</h3>
                                        <p className="text-sm text-gray-500">Due: {milestone.payment.date}</p>
                                    </div>
                                    <div className={`text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm 
                    ${milestone.status === 'complete' ? 'bg-[#d1fae5] text-[#065f46]' :
                                            milestone.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                                'bg-gray-100 text-gray-800'}`
                                    }>
                                        {milestone.status}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <h4 className="text-sm font-medium mb-2">Deliverables</h4>
                                        <div className="space-y-2">
                                            {milestone.deliverables.map((deliverable, idx) => (
                                                <div key={idx} className="flex items-center gap-2 text-sm">
                                                    <CheckSquare className="w-4 h-4 text-gray-400" />
                                                    {deliverable}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium mb-2">Payment Details</h4>
                                        <div className="p-3 bg-gray-50 rounded-lg">
                                            <div className="text-2xl font-bold mb-1">
                                                £{milestone.payment.amount}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                Status: {milestone.payment.released ? 'Released' : 'Pending'}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end gap-2">
                                    {!milestone.payment.released && milestone.status === 'in_progress' && (
                                        <>
                                            <button className="flex items-center gap-2 px-4 py-2 rounded-lg border border-green text-green">
                                                Review Deliverables</button>
                                            <button className="flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                                                Release Payment</button>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}
export default Main